import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  deleteInstruction,
  errorMessage,
  fetchInstructionFile,
  fetchInstructions,
  formatDateTime,
  PackingInstruction,
  saveInstruction,
} from './packingApi';
import { btn, card, dialog, input, overlay } from './packingStyles';

/** pdf.js is large — loaded only when an instruction is opened. */
const PdfViewer = lazy(() => import('../common/PdfViewer'));

/** Same limit as the backend (PackingInstructionRequest::MAX_FILE_KB). */
const MAX_FILE_BYTES = 20 * 1024 * 1024;

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(bytes / 1024))} kB`;

interface Props {
  onMessage: (text: string, error?: boolean) => void;
}

interface Form {
  id?: number;
  scope: 'brand' | 'index';
  brand: string;
  catalogIndex: string;
  file: File | null;
  /** Name of the PDF the instruction has now (editing). */
  currentFile: string | null;
}

const EMPTY: Form = { scope: 'brand', brand: '', catalogIndex: '', file: null, currentFile: null };

/**
 * "Instrukcje pakowania" (manager): one PDF instruction per brand, optionally a different one for a
 * single index. The packer opens the matching one full screen from the "Pakuj" dialog.
 */
const PackingInstructionsView: React.FC<Props> = ({ onMessage }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState<PackingInstruction[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<PackingInstruction | null>(null);
  const previewId = preview?.id;
  const loadPreview = useCallback(() => fetchInstructionFile(previewId as number), [previewId]);
  const closePreview = useCallback(() => setPreview(null), []);

  const load = useCallback(async () => {
    try {
      const result = await fetchInstructions();
      setItems(result.data);
      setBrands(result.meta.brands);
    } catch (e: any) {
      onMessage(errorMessage(e, t('packing.errorLoad')), true);
    }
  }, [onMessage, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Brands come only from production (no typing a new one); a brand with an instruction can not be
  // picked again — except by the instruction being edited, which also keeps a brand production dropped.
  const brandOptions = form?.brand && !brands.includes(form.brand) ? [form.brand, ...brands] : brands;
  const takenBrands = new Set(
    items
      .filter((i) => i.scope === 'brand' && i.id !== form?.id && i.brand)
      .map((i) => (i.brand as string).trim().toLowerCase()),
  );

  const open = (i?: PackingInstruction) => {
    setError(null);
    setForm(
      i
        ? { id: i.id, scope: i.scope, brand: i.brand ?? '', catalogIndex: i.catalog_index ?? '', file: null, currentFile: i.file_name }
        : EMPTY,
    );
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    if (!form.id && !form.file) {
      setError(t('packing.instructions.fileRequired'));
      return;
    }
    if (form.file && form.file.size > MAX_FILE_BYTES) {
      setError(t('packing.instructions.fileTooBig'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveInstruction(
        {
          brand: form.scope === 'brand' ? form.brand : null,
          catalog_index: form.scope === 'index' ? form.catalogIndex : null,
          file: form.file,
        },
        form.id,
      );
      setForm(null);
      onMessage(t('packing.saved'));
      await load();
    } catch (err: any) {
      setError(errorMessage(err, t('packing.errorSave')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (i: PackingInstruction) => {
    if (!window.confirm(t('packing.instructions.confirmDelete'))) return;
    try {
      await deleteInstruction(i.id);
      onMessage(t('packing.saved'));
      await load();
    } catch (err: any) {
      onMessage(errorMessage(err, t('packing.errorSave')), true);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 16 }}>
        <p style={{ margin: 0, color: '#475569', fontSize: 14, flex: '1 1 320px' }}>{t('packing.instructions.hint')}</p>
        <button type="button" style={btn('primary')} onClick={() => open()}>+ {t('packing.instructions.add')}</button>
      </div>
      {items.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>{t('packing.instructions.empty')}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {items.map((i) => (
            <div key={i.id} style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>
                    {i.scope === 'index' ? t('packing.instructions.scopeIndex') : t('packing.instructions.scopeBrand')}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', fontFamily: i.scope === 'index' ? 'monospace' : undefined }}>
                    {i.scope === 'index' ? i.catalog_index : i.brand}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" style={btn('ghost', true)} onClick={() => open(i)}>{t('packing.reasons.edit')}</button>
                  <button type="button" style={btn('danger', true)} onClick={() => void remove(i)}>{t('packing.details.delete')}</button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPreview(i)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer',
                  border: '1px solid #e2e8f0', borderLeft: '4px solid #667eea', borderRadius: 8, background: '#f8fafc', padding: '10px 12px',
                }}
              >
                <span aria-hidden style={{ fontSize: 13, fontWeight: 800, color: '#fff', background: '#dc2626', borderRadius: 6, padding: '6px 7px' }}>PDF</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.file_name}</span>
                  <span style={{ display: 'block', fontSize: 12, color: '#64748b' }}>{formatSize(i.file_size)} · {t('packing.instructions.open')}</span>
                </span>
              </button>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                {t('packing.instructions.updated', { when: formatDateTime(i.updated_at), who: i.updated_by ?? '—' })}
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div style={overlay}>
          <form style={dialog(560)} onSubmit={save}>
            <h2 style={{ margin: '0 0 14px', fontSize: 19, color: '#0f172a' }}>
              {form.id ? t('packing.instructions.editTitle') : t('packing.instructions.addTitle')}
            </h2>
            <div role="radiogroup" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {(['brand', 'index'] as const).map((scope) => (
                <button
                  key={scope}
                  type="button"
                  role="radio"
                  aria-checked={form.scope === scope}
                  style={btn(form.scope === scope ? 'primary' : 'ghost')}
                  onClick={() => setForm({ ...form, scope })}
                >
                  {scope === 'brand' ? t('packing.instructions.scopeBrand') : t('packing.instructions.scopeIndex')}
                </button>
              ))}
            </div>
            {form.scope === 'brand' ? (
              <label style={{ display: 'block', marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#334155' }}>
                {t('packing.col.brand')}
                <select
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  required
                  style={{ ...input, marginTop: 6 }}
                >
                  <option value="">{t('packing.instructions.pickBrand')}</option>
                  {brandOptions.map((b) => {
                    const taken = takenBrands.has(b.toLowerCase());
                    return (
                      <option key={b} value={b} disabled={taken}>
                        {taken ? t('packing.instructions.brandTaken', { brand: b }) : b}
                      </option>
                    );
                  })}
                </select>
              </label>
            ) : (
              <label style={{ display: 'block', marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#334155' }}>
                {t('packing.col.index')}
                <input
                  value={form.catalogIndex}
                  onChange={(e) => setForm({ ...form, catalogIndex: e.target.value })}
                  maxLength={255}
                  required
                  style={{ ...input, marginTop: 6, fontFamily: 'monospace' }}
                />
                <span style={{ display: 'block', marginTop: 4, fontSize: 12, fontWeight: 400, color: '#64748b' }}>{t('packing.instructions.indexHint')}</span>
              </label>
            )}
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600, color: '#334155' }}>
              {t('packing.instructions.file')}
              <input
                type="file"
                accept="application/pdf,.pdf"
                required={!form.id}
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] ?? null })}
                style={{ ...input, marginTop: 6, padding: '9px 10px' }}
              />
            </label>
            <span style={{ display: 'block', fontSize: 12, color: '#64748b' }}>
              {form.currentFile
                ? t('packing.instructions.fileCurrent', { name: form.currentFile })
                : t('packing.instructions.fileHint')}
            </span>
            {error && (
              <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 14, marginTop: 12 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" style={btn('ghost')} onClick={() => setForm(null)}>{t('packing.modal.cancel')}</button>
              <button type="submit" style={{ ...btn('primary'), opacity: saving ? 0.6 : 1 }} disabled={saving}>{t('packing.modal.save')}</button>
            </div>
          </form>
        </div>
      )}

      {preview && (
        <Suspense fallback={null}>
          <PdfViewer title={preview.file_name} load={loadPreview} onClose={closePreview} />
        </Suspense>
      )}
    </>
  );
};

export default PackingInstructionsView;
